import { useState, useEffect, useCallback, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Star, Quote, ShieldCheck, ChevronLeft, ChevronRight, ArrowUpRight, X } from "lucide-react";

/**
 * Real Google reviews for Wertbild Immobilienfotografie.
 * Order matches the "Bewertungen Stand 08.07.2026.pdf" exactly.
 * All reviewers have 5 stars.
 */
const TESTIMONIALS = [
  {
    name: "Daniel Rogge",
    date: "vor 2 Monaten",
    avatar: { type: "photo" },
    text:
      "Beim Verkauf unseres Elternhauses durften wir mit Herrn Bäcker zusammenarbeiten und möchten uns auf diesem Weg noch einmal ausdrücklich für die hervorragende Unterstützung bedanken.\n\nBesonders beeindruckt hat uns die professionelle Präsentation unserer Immobilie. Das Exposé, die hochwertigen Fotos, Immobilienvideos, modernen Drohnenaufnahmen und der 3D-Rundgang haben unser Haus perfekt in Szene gesetzt und einen erstklassigen Eindruck vermittelt. Man spürt sofort, wie viel Erfahrung, Kreativität und Gespür für wirkungsvolle Immobilienfotografie dahinterstecken.\n\nZuvor hatten wir selbst versucht, die Immobilie ansprechend zu präsentieren. Doch selbst mit einem Innendesign-Studium in der Familie wurde schnell deutlich, welchen Unterschied professionelle Umsetzung und echte Erfahrung in der Praxis machen. Gerade die hochwertige Präsentation und das professionelle Immobilienmarketing haben entscheidend dazu beigetragen, dass unser Haus trotz der schwierigen Marktsituation und der hohen Zinsen zu einem sehr guten Preis verkauft werden konnte.\n\nWir können Herrn Bäcker uneingeschränkt weiterempfehlen — besonders für alle, die Wert auf eine hochwertige und professionelle Immobilienvermarktung legen.",
  },
  {
    name: "Real AixState GmbH",
    date: "vor 2 Monaten",
    avatar: { type: "letter", letter: "R", bg: "#3B82F6", color: "#FFFFFF" },
    text:
      "Seit über 3 Jahren arbeiten wir als Immobilienmaklerbüro mit Wertbild Immobilienfotografie zusammen und haben bereits mehr als 60 Immobilien gemeinsam umgesetzt.\n\nDie Zusammenarbeit ist für uns nicht nur professionell, sondern auch menschlich sehr angenehm. Besonders schätzen wir das Gespür für eine hochwertige und wirkungsvolle Präsentation von Immobilien.\n\nHerr Bäcker bringt dabei nicht nur fachliche Expertise mit, sondern durch sein Know-how als Immobilienmakler und seinen Hintergrund in der Wirtschaftspsychologie auch ein tiefes Verständnis dafür, worauf es bei einer erfolgreichen Vermarktung wirklich ankommt.\n\nOb Immobilienfotografie, Immobilienvideos, Drohnenaufnahmen, virtuelles Home Staging oder 3D-Rundgänge: Für unser Immobilienmarketing ist das eine echte Bereicherung. Die Ergebnisse schaffen einen starken ersten Eindruck und begeistern unsere Eigentümer und Kaufinteressenten immer wieder aufs Neue.\n\nFür uns eine klare 100 % Weiterempfehlung.",
  },
  {
    name: "Wiehad kh",
    date: "vor 2 Monaten",
    avatar: { type: "letter", letter: "W", bg: "#1F1F1F", color: "#F5F2EC" },
    text:
      "Wir haben unser Mehrfamilienhaus fotografieren lassen und sind wirklich begeistert vom Ergebnis. Das Team von Wertbild Immobilienfotografie hat sehr professionell gearbeitet und genau verstanden, worauf es bei der Präsentation einer Immobilie ankommt.\n\nBesonders überzeugt haben uns die professionellen Immobilienfotos inklusive Drohnenaufnahmen sowie 360° Rundgang. Dadurch wirkte alles hochwertig, modern und sehr ansprechend. Gerade in Düren ist es nicht leicht, so einen guten und zuverlässigen Service zu finden.\n\nAbsolute Empfehlung für alle, die ihre Immobilie bestmöglich vermarkten möchten!",
  },
  {
    name: "ydemir",
    date: "vor einem Monat",
    avatar: { type: "letter", letter: "y", bg: "#F59E0B", color: "#FFFFFF" },
    text:
      "Höchste Professionalität auf allen Ebenen. Gerade beim Immobilienverkauf sind hochwertige Bilder von enormer Bedeutung. Herr Bäcker zeigt mit Wertbild Immobilienfotografie, wie wirkungsvoll eine professionelle Präsentation von Immobilien sein kann.\n\nDie Fotos für den Hausverkauf zeigen nicht nur, sie begeistern. Die Leidenschaft für den Beruf ist dabei in jedem Detail spürbar.\n\nFür mich genau das, was moderne Immobilienvermarktung heute ausmacht. Eine ganz klare 1000 % Empfehlung.",
  },
  {
    name: "Silvanus Ek",
    date: "vor 2 Monaten",
    avatar: { type: "letter", letter: "S", bg: "#DC2626", color: "#FFFFFF" },
    text:
      "Vom ersten Moment an war spürbar, dass hier nicht einfach fotografiert wird, sondern mit echter Leidenschaft, feinem Gespür und einem hohen Anspruch an Wirkung gearbeitet wird.\n\nHier entstehen keine bloßen Aufnahmen, sondern visuelle Arbeiten mit Wirkung, Präzision und Ausdruck. Eine Form der Immobilienfotografie, die dem Namen Wertbild auf besondere Weise gerecht wird.\n\nDer Inhaber begegnet einem mit außergewöhnlicher Freundlichkeit, großer Professionalität und einer spürbaren Hingabe für Qualität. Dadurch entsteht vom ersten Augenblick an ein Gefühl von Vertrauen.\n\nEine klare, uneingeschränkte und von Herzen kommende Empfehlung für alle, die einen Premium Immobilienfotografen suchen.",
  },
  {
    name: "Wadim",
    date: "vor 2 Monaten",
    avatar: { type: "photo" },
    text:
      "Auf Grund der aussagekräftigen und detaillierten Fotos einer Immobilie habe ich mich dazu entschieden sie schlussendlich zu besichtigen. Sehr gute Arbeit!",
  },
  {
    name: "Kevin B.",
    date: "vor einer Woche",
    avatar: { type: "letter", letter: "K", bg: "#7C3AED", color: "#FFFFFF" },
    text:
      "Ein wirklich herausragender Fotograf für Immobilien! Es wird immer mitgedacht, zügig und professionell gearbeitet und die Qualität der Aufnahmen überzeugt jedes Mal aufs Neue. Vielen Dank für die tolle Arbeit und die durchweg hervorragenden Ergebnisse!",
  },
  {
    name: "Mr. Duman",
    date: "vor 2 Monaten",
    avatar: { type: "letter", letter: "M", bg: "#6B7280", color: "#F5F2EC" },
    text:
      "Sehr nett und vertrauenswürdig. Zudem ein sehr leidenschaftlicher Fotograf. Die Zusammenarbeit war sehr angenehm. Immer wieder gerne.",
  },
  {
    name: "Ewelin Kosmann",
    date: "vor 2 Monaten",
    avatar: { type: "letter", letter: "E", bg: "#059669", color: "#FFFFFF" },
    text:
      "Ich bin wirklich begeistert vom Fotoservice von Artur Bäcker / Wertbild Immobilienfotos. Professionell, schnell und absolut zuverlässig. Genau so stellt man sich moderne Immobilienfotografie vor. Klare Empfehlung!",
  },
  {
    name: "Anna N.",
    date: "vor 2 Monaten",
    avatar: { type: "photo" },
    text:
      "Sehr netter und angenehmer Kontakt. Man merkt direkt, dass hier mit viel Gespür für gute Ergebnisse gearbeitet wird.\n\nBesonders auffällig sind die verkaufsfördernden Immobilienfotos, die wirklich dafür sorgen, dass Objekte gut und ansprechend präsentiert werden.\n\nDie Kommunikation ist unkompliziert, freundlich und sehr zuverlässig. Insgesamt ein sehr positiver Eindruck von jemandem, der seinen Job ernst nimmt und mit viel Engagement dabei ist.\n\nKann ich auf jeden Fall nur weiterempfehlen!",
  },
  {
    name: "Ruth Mc",
    date: "vor 2 Monaten",
    avatar: { type: "letter", letter: "R", bg: "#2563EB", color: "#FFFFFF" },
    text:
      "Sehr vertrauensvoller Fotograf für Immobilien und ein sehr angenehmer Kontakt! Nur zu empfehlen. :) Man fühlt sich stets gut aufgehoben.",
  },
];

const GOOGLE_MAPS_URL =
  "https://www.google.com/maps/place/Wertbild+Immobilienfotografie/@50.8227342,6.5684712,15z/data=!4m6!3m5!1s0x2b343a7a882c1bb9:0x456c22c4a55557e3!8m2!3d50.8227342!4d6.5684712!16s%2Fg%2F11zkwfkvj8";

function initials(name) {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Avatar({ avatar, name, size = 48 }) {
  const dim = { width: size, height: size, fontSize: size / 2.3 };
  if (avatar.type === "letter") {
    return (
      <div
        className="rounded-full flex items-center justify-center font-medium shrink-0"
        style={{ ...dim, backgroundColor: avatar.bg, color: avatar.color }}
        aria-hidden="true"
      >
        {avatar.letter}
      </div>
    );
  }
  return (
    <div
      className="rounded-full flex items-center justify-center font-medium shrink-0 bg-gradient-to-br from-[#3A3532] to-[#1A1A1A] text-[#F5F2EC]"
      style={dim}
      aria-hidden="true"
    >
      {initials(name)}
    </div>
  );
}

function GoogleG({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

/** Full-screen modal that shows the entire review. Closes on Escape, X, and overlay click. */
function ReviewModal({ testimonial, onClose }) {
  const overlayRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  if (!testimonial) return null;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      data-testid="review-modal-overlay"
      className="fixed inset-0 z-[100] bg-[#0E0E0E]/70 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 animate-[fadeIn_0.25s_ease-out]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="review-modal-title"
    >
      <div
        className="relative bg-[#F8F5EF] border border-[#EAE6DE] w-full max-w-[720px] max-h-[90vh] overflow-y-auto shadow-[0_28px_80px_-24px_rgba(0,0,0,0.5)] animate-[modalIn_0.35s_cubic-bezier(0.16,1,0.3,1)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Schließen"
          data-testid="review-modal-close"
          className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center bg-[#F8F5EF] border border-[#EAE6DE] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#F8F5EF] hover:border-[#1A1A1A] transition-colors z-10"
        >
          <X size={18} />
        </button>

        <div className="p-8 md:p-12">
          {/* Header */}
          <div className="flex items-start gap-4 pr-14">
            <Avatar avatar={testimonial.avatar} name={testimonial.name} size={56} />
            <div className="flex-1 min-w-0">
              <h3 id="review-modal-title" className="font-display text-2xl md:text-3xl text-[#1A1A1A] leading-tight">
                {testimonial.name}
              </h3>
              <div className="mt-1.5 text-[12px] text-[#5A5A5A] tracking-wide">{testimonial.date}</div>
            </div>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={17} className="fill-[#FBBC05] text-[#FBBC05]" />
              ))}
            </div>
            <GoogleG size={14} />
            <span className="text-[11px] uppercase tracking-[0.24em] text-[#5A5A5A]">Google-Bewertung</span>
            <span
              className="ml-auto inline-flex items-center gap-1.5 bg-[#FFA500]/12 text-[#FFA500] px-2.5 py-1 text-[9px] uppercase tracking-[0.24em] font-medium border border-[#FFA500]/25"
              title="Verifizierte Google-Bewertung"
            >
              <ShieldCheck size={11} strokeWidth={2.2} />
              Verifiziert
            </span>
          </div>

          <Quote size={26} className="text-[#FFA500] mt-8 mb-4" strokeWidth={1.5} />

          <div className="font-display italic text-[17px] md:text-[19px] leading-[1.6] text-[#1A1A1A] whitespace-pre-line">
            {`„${testimonial.text}"`}
          </div>

          <div className="mt-10 pt-6 border-t border-[#EAE6DE] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-[12px] text-[#5A5A5A] font-light">
              Diese Bewertung stammt aus dem verifizierten Google-Business-Profil von Wertbild.
            </div>
            <a
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="review-modal-google-link"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-medium text-[#1A1A1A] hover:text-[#FFA500] transition-colors"
            >
              Auf Google ansehen <ArrowUpRight size={13} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function TestimonialCard({ t, i, onOpen }) {
  return (
    <article
      data-testid={`testimonial-${i}`}
      className="pl-6 md:pl-8 shrink-0 w-full sm:w-[90%] md:w-1/2 lg:w-[38%]"
    >
      <div className="group relative bg-white border border-[#EAE6DE] p-8 md:p-10 h-full flex flex-col shadow-[0_18px_40px_-24px_rgba(0,0,0,0.15)] hover:border-[#FFA500]/50 hover:shadow-[0_28px_60px_-24px_rgba(255,165,0,0.25)] hover:z-20 transition-all duration-500">
        {/* Top row */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <Avatar avatar={t.avatar} name={t.name} />
            <div>
              <div className="text-[#1A1A1A] font-medium leading-tight">{t.name}</div>
              <div className="text-[11px] text-[#5A5A5A] mt-1 tracking-wide">{t.date}</div>
            </div>
          </div>
          <div
            className="inline-flex items-center gap-1.5 bg-[#FFA500]/12 text-[#FFA500] px-2.5 py-1 text-[9px] uppercase tracking-[0.24em] font-medium border border-[#FFA500]/25"
            title="Verifizierte Google-Bewertung"
          >
            <ShieldCheck size={11} strokeWidth={2.2} />
            Verifiziert
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <div className="flex gap-1">
            {[...Array(5)].map((_, idx) => (
              <Star key={idx} size={15} className="fill-[#FBBC05] text-[#FBBC05]" />
            ))}
          </div>
          <GoogleG size={13} />
        </div>

        <Quote size={22} className="text-[#FFA500] mt-6 mb-3" strokeWidth={1.5} />

        {/* Text: 3 lines by default; on desktop hover expands smoothly to full 12 lines. */}
        <p
          className="font-display italic text-[17px] md:text-[18px] leading-[1.55] text-[#1A1A1A] flex-1 whitespace-pre-line testimonial-clamp md:group-hover:testimonial-expand"
        >
          {`„${t.text}"`}
        </p>

        {/* Read more link */}
        <button
          onClick={() => onOpen(i)}
          data-testid={`testimonial-read-more-${i}`}
          className="group/btn mt-6 inline-flex items-center gap-2 self-start text-[11px] uppercase tracking-[0.2em] font-medium text-[#1A1A1A] hover:text-[#FFA500] transition-colors"
        >
          <span className="link-underline">Mehr lesen</span>
          <ArrowUpRight size={13} className="transition-transform duration-300 group-hover/btn:rotate-45" />
        </button>
      </div>
    </article>
  );
}

export default function Testimonials() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: "start", slidesToScroll: 1 });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const [modalIndex, setModalIndex] = useState(null);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
    };
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();
    return () => { emblaApi.off("select", onSelect); };
  }, [emblaApi]);

  const openModal = (i) => setModalIndex(i);
  const closeModal = () => setModalIndex(null);

  return (
    <section data-testid="testimonials-section" className="py-24 md:py-32 bg-[#F5F2EC]/40">
      <div className="container-x">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 md:mb-16">
          <div className="md:col-span-7 reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="overline">Echte Kundenstimmen</span>
            </div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-[#1A1A1A] tracking-tight">
              Was Kunden <br />
              <span className="italic font-light">sagen</span>.
            </h2>
          </div>

          {/* Trust bar (clickable to Google Maps) */}
          <div className="md:col-span-5 md:justify-self-end reveal">
            <a
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="trust-box-google-link"
              className="group inline-flex flex-col gap-3 bg-white border border-[#EAE6DE] px-6 py-5 hover:border-[#FFA500] hover:shadow-[0_18px_40px_-24px_rgba(255,165,0,0.4)] transition-all duration-300"
              aria-label="Alle Google-Bewertungen auf Google Maps ansehen"
            >
              <div className="flex items-center gap-3">
                <GoogleG size={22} />
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="fill-[#FBBC05] text-[#FBBC05]" />
                  ))}
                </div>
                <span className="ticker-num text-2xl text-[#1A1A1A]">5,0</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-[12px] text-[#5A5A5A] tracking-wide">
                  Basierend auf <span className="text-[#1A1A1A] font-medium">Google-Bewertungen</span>
                </span>
                <span className="text-[11px] uppercase tracking-[0.18em] text-[#FFA500] font-medium inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  Ansehen <ArrowUpRight size={12} />
                </span>
              </div>
            </a>
          </div>
        </div>

        {/* Slider */}
        <div className="relative reveal">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-6 md:gap-8 -ml-6 md:-ml-8 items-stretch">
              {TESTIMONIALS.map((t, i) => (
                <TestimonialCard key={t.name} t={t} i={i} onOpen={openModal} />
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="mt-8 md:mt-10 flex items-center justify-between gap-6">
            <div className="flex gap-2">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => scrollTo(i)}
                  aria-label={`Bewertung ${i + 1}`}
                  data-testid={`testimonial-dot-${i}`}
                  className={`h-[3px] transition-all duration-300 ${
                    selectedIndex === i ? "w-8 bg-[#FFA500]" : "w-4 bg-[#1A1A1A]/25 hover:bg-[#1A1A1A]/50"
                  }`}
                />
              ))}
            </div>
            <div className="flex gap-3">
              <button
                onClick={scrollPrev}
                disabled={!canPrev}
                aria-label="Vorherige Bewertung"
                data-testid="testimonial-prev"
                className={`w-11 h-11 flex items-center justify-center border border-[#1A1A1A]/20 transition-colors ${
                  canPrev ? "hover:bg-[#1A1A1A] hover:text-[#F8F5EF] hover:border-[#1A1A1A]" : "opacity-30 cursor-not-allowed"
                }`}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={scrollNext}
                disabled={!canNext}
                aria-label="Nächste Bewertung"
                data-testid="testimonial-next"
                className={`w-11 h-11 flex items-center justify-center border border-[#1A1A1A]/20 transition-colors ${
                  canNext ? "hover:bg-[#1A1A1A] hover:text-[#F8F5EF] hover:border-[#1A1A1A]" : "opacity-30 cursor-not-allowed"
                }`}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {modalIndex !== null && (
        <ReviewModal testimonial={TESTIMONIALS[modalIndex]} onClose={closeModal} />
      )}
    </section>
  );
}
